import React, { useEffect, useState } from "react";
import { ChevronDown, Plus, Trash2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { currentPlaces, applyPlaces, currentReklame, applyReklame } from "@/lib/places";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";

function CountryRow({ c, i, onChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const cities = Object.keys(c.cities || {});

  const [prog, setProg] = useState("");
  const addCity = async () => {
    const names = [...new Set(city.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean))].filter((n) => !(n in (c.cities || {})));
    if (!names.length) return toast.error(city.trim() ? "Këto qytete ekzistojnë" : "Shkruaj qytetet");
    setBusy(true);
    const add = { ...(c.cities || {}) };
    const pts = Object.values(add).filter(Boolean);
    const center = pts.length ? { lat: pts.reduce((s, p) => s + p[0], 0) / pts.length, lng: pts.reduce((s, p) => s + p[1], 0) / pts.length } : {};
    let miss = 0;
    for (let k = 0; k < names.length; k++) {
      setProg(`${k + 1}/${names.length}`);
      try { const { data } = await api.get("/admin/geocode", { params: { q: names[k], country: c.name, ...center } }); add[names[k]] = data.coords; }
      catch { add[names[k]] = null; miss++; }
    }
    onChange({ ...c, cities: add }, true);
    setCity(""); setProg(""); setBusy(false);
    toast.success(`U shtuan ${names.length} qytete` + (miss ? ` (${miss} pa vendndodhje në hartë)` : ""));
  };
  const delCity = (name) => { const n = { ...c.cities }; delete n[name]; onChange({ ...c, cities: n }, true); };

  return (
    <div className="bg-white rounded-2xl border border-slate-100" data-testid={`place-country-${i}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`place-country-toggle-${i}`} className="w-full flex items-center gap-2 p-3 text-left">
        <span className="text-xl">{c.flag}</span>
        <span className="flex-1 font-semibold text-sm text-slate-800">{c.name} <span className="text-slate-400 font-normal">{c.dial}</span></span>
        <span className="text-xs text-slate-400">{cities.length} qytete</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="grid grid-cols-[60px_1fr_80px] gap-2">
            <Input data-testid={`place-flag-${i}`} value={c.flag} onChange={(e) => onChange({ ...c, flag: e.target.value })} className="rounded-xl h-10 text-center" placeholder="🏳️" />
            <Input data-testid={`place-name-${i}`} value={c.name} onChange={(e) => onChange({ ...c, name: e.target.value })} className="rounded-xl h-10" />
            <Input data-testid={`place-dial-${i}`} value={c.dial} onChange={(e) => onChange({ ...c, dial: e.target.value })} className="rounded-xl h-10" placeholder="+49" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {cities.map((n) => (
              <span key={n} className="flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full pl-2.5 pr-1 py-1">
                {n}
                <button type="button" onClick={() => delCity(n)} data-testid={`place-del-city-${i}-${n}`} aria-label="Hiq"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="space-y-2">
            <textarea data-testid={`place-new-city-${i}`} value={city} onChange={(e) => setCity(e.target.value)} rows={3} placeholder={"Shto qytet... Mund të ngjitësh shumë njëherësh:\nnjë qytet në çdo rresht (ose me presje)"} className="w-full rounded-xl border border-slate-200 p-3 text-sm" />
            <Button type="button" onClick={addCity} disabled={busy} data-testid={`place-add-city-${i}`} className="w-full rounded-xl h-10 bg-emerald-600 hover:bg-emerald-700">{busy ? `Duke shtuar ${prog}...` : <><Plus className="w-4 h-4 mr-1" />Shto qytetet{city.split(/[\n,;]+/).filter((s) => s.trim()).length > 1 ? ` (${city.split(/[\n,;]+/).filter((s) => s.trim()).length})` : ""}</>}</Button>
          </div>
          <button type="button" onClick={onDelete} data-testid={`place-del-country-${i}`} className="flex items-center gap-1 text-xs font-semibold text-red-500"><Trash2 className="w-3.5 h-3.5" />Hiq shtetin</button>
        </div>
      )}
    </div>
  );
}

export default function AdminPlaces({ kind = "main" }) {
  const reklame = kind === "reklame";
  const { confirm } = useConfirm();
  const [list, setList] = useState(null);
  const [nc, setNc] = useState({ flag: "", name: "", dial: "" });
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/places").then(({ data }) => {
      applyPlaces(data.countries); applyReklame(data.reklame);
      setList(reklame ? currentReklame() : currentPlaces());
    }).catch(() => setFailed(true));
  }, [reklame]);

  const addCountry = () => {
    if (!nc.name.trim()) return toast.error("Shkruaj emrin e shtetit");
    if (list.some((c) => c.name.toLowerCase() === nc.name.trim().toLowerCase())) return toast.error("Ky shtet ekziston");
    const dial = nc.dial.trim() && !nc.dial.trim().startsWith("+") ? `+${nc.dial.trim()}` : nc.dial.trim();
    const next = [...list, { name: nc.name.trim(), flag: nc.flag.trim(), dial, cities: {} }];
    setList(next);
    setNc({ flag: "", name: "", dial: "" });
    save(next);
  };
  const delCountry = async (i) => {
    if (!(await confirm({ title: "Hiq shtetin?", message: `${list[i].name} dhe qytetet e tij do të hiqen.`, confirmText: "Hiq" }))) return;
    const next = list.filter((_, x) => x !== i);
    setList(next);
    save(next);
  };
  const save = async (arg) => {
    const countries = Array.isArray(arg) ? arg : list;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/places", { countries }, { params: { kind } });
      (reklame ? applyReklame : applyPlaces)(data.countries);
      setList(data.countries);
      toast.success("Shtetet dhe qytetet u ruajtën");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!list) return <div data-testid="admin-places-loading" className="p-6 text-center text-sm text-slate-500">{failed ? "Shtetet nuk u ngarkuan. Rifresko faqen." : "Duke ngarkuar shtetet..."}</div>;

  return (
    <div className="space-y-2 pb-20" data-testid={reklame ? "admin-places-reklame" : "admin-places"}>
      {reklame && <p className="text-xs text-slate-500 px-1">Këto shtete dhe qytete përdoren vetëm te kategoria Reklamë.</p>}
      <div className="bg-white rounded-2xl border border-slate-100 p-3">
        <h3 className="text-sm font-bold text-slate-900 mb-2">Shto shtet</h3>
        <div className="grid grid-cols-[60px_1fr_80px] gap-2">
          <Input data-testid="place-new-flag" value={nc.flag} onChange={(e) => setNc({ ...nc, flag: e.target.value })} placeholder="🇩🇪" className="rounded-xl h-10 text-center" />
          <Input data-testid="place-new-name" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder="Emri i shtetit" className="rounded-xl h-10" />
          <Input data-testid="place-new-dial" value={nc.dial} onChange={(e) => setNc({ ...nc, dial: e.target.value })} placeholder="+49" className="rounded-xl h-10" />
        </div>
        <Button type="button" onClick={addCountry} data-testid="place-add-country" className="w-full mt-2 rounded-xl h-10 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4 mr-1" />Shto shtetin</Button>
      </div>
      {list.map((c, i) => (
        <CountryRow key={i} c={c} i={i} onChange={(n, persist) => { const next = list.map((x, j) => (j === i ? n : x)); setList(next); if (persist) save(next); }} onDelete={() => delCountry(i)} />
      ))}
      <Button onClick={save} disabled={saving} data-testid="places-save" className="sticky bottom-24 w-full rounded-xl h-12 bg-emerald-600 hover:bg-emerald-700 shadow-lg">{saving ? "Duke ruajtur..." : "Ruaj ndryshimet"}</Button>
    </div>
  );
}
